import { nextTick, onBeforeUnmount, onMounted, unref } from 'vue'
import { getActiveContext, getAllContexts } from '~/components/core/Page/context'
import { breakpoints } from '~/config'

const SVG_NS = 'http://www.w3.org/2000/svg'
const GENERATED_CONNECTOR_ATTR = 'data-generated-segment-connectors'
const START_ANCHOR_ATTR = 'data-anchor-start'
const END_ANCHOR_ATTR = 'data-anchor-end'
const REVERSE_ANCHOR_ATTR = 'data-anchor-reverse'
const TERMINAL_TARGET_ATTR = 'data-terminal-target'
const TERMINAL_ANCHOR_ATTR = 'data-terminal-anchor'
const TERMINAL_ID_ATTR = 'data-segment-terminal'
const SEGMENT_PROFILE_ATTR = 'data-segment-profile'
const TERMINAL_PROFILE_ATTR = 'data-terminal-profile'
const CONNECTOR_FROM_ORDER_ATTR = 'data-connector-from-order'
const CONNECTOR_TO_ORDER_ATTR = 'data-connector-to-order'
const CONNECTOR_TERMINAL_TARGET_ATTR = 'data-connector-terminal-target'

const clamp01 = (value) => Math.min(1, Math.max(0, value))
const parsePositiveNumber = (value) => {
	const parsedValue = Number.parseFloat(`${value}`)
	return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null
}

const parseNormalizedAnchor = (rawValue) => {
	if (typeof rawValue !== 'string') return null

	const [rawX, rawY] = rawValue
		.split(',')
		.map((part) => part?.trim())
		.filter((part) => part?.length)

	const x = Number.parseFloat(rawX)
	const y = Number.parseFloat(rawY)
	if (!Number.isFinite(x) || !Number.isFinite(y)) return null

	return { x: clamp01(x), y: clamp01(y) }
}

const parseBooleanAttribute = (rawValue) => {
	if (typeof rawValue !== 'string') return false

	const normalized = rawValue.trim().toLowerCase()
	return normalized === '' || normalized === 'true' || normalized === '1' || normalized === 'yes'
}

const getAttrTrimmed = (element, attrName) => {
	const rawValue = element.getAttribute(attrName)
	if (typeof rawValue !== 'string') return ''
	return rawValue.trim()
}

const matchesProfile = (element, attrName, activeProfile) => {
	if (!activeProfile) return false

	const rawProfile = getAttrTrimmed(element, attrName)
	if (!rawProfile) return true

	return rawProfile.toLowerCase() === activeProfile
}

const isElementVisible = (element) => {
	if (!(element instanceof Element)) return false

	const style = window.getComputedStyle(element)
	if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false

	const rect = element.getBoundingClientRect()
	return rect.width > 0 && rect.height > 0
}

const isDisplayVisible = (element) => {
	if (!(element instanceof Element)) return false

	const style = window.getComputedStyle(element)
	return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse'
}

const toViewportPointFromPath = (path, length) => {
	const matrix = path.getScreenCTM?.()
	if (!matrix) return null

	const point = path.getPointAtLength(length)

	if (typeof DOMPoint === 'function') {
		const transformed = new DOMPoint(point.x, point.y).matrixTransform(matrix)
		return { x: transformed.x, y: transformed.y }
	}

	const ownerSvg = path.ownerSVGElement
	if (!ownerSvg?.createSVGPoint) return null

	const svgPoint = ownerSvg.createSVGPoint()
	svgPoint.x = point.x
	svgPoint.y = point.y
	const transformed = svgPoint.matrixTransform(matrix)
	return { x: transformed.x, y: transformed.y }
}

const getFirstUsablePath = (segment) => {
	if (!(segment instanceof SVGSVGElement)) return null

	const paths = Array.from(segment.querySelectorAll('path'))
	for (const path of paths) {
		if (!(path instanceof SVGPathElement)) continue
		if (!isDisplayVisible(path)) continue

		try {
			const totalLength = path.getTotalLength()
			if (!Number.isFinite(totalLength)) continue
			return path
		} catch {
			continue
		}
	}

	return null
}

const parseAnchorOverride = (segment, attributeName) => {
	const parsed = parseNormalizedAnchor(segment.getAttribute(attributeName))
	if (!parsed) return null

	const rect = segment.getBoundingClientRect()
	if (rect.width <= 0 || rect.height <= 0) return null

	return {
		x: rect.left + rect.width * parsed.x,
		y: rect.top + rect.height * parsed.y,
	}
}

const resolveAnchorPoint = (segment, type) => {
	const reverseAnchors = parseBooleanAttribute(segment.getAttribute(REVERSE_ANCHOR_ATTR))
	const effectiveType = reverseAnchors ? (type === 'start' ? 'end' : 'start') : type
	const overrideAttribute = effectiveType === 'start' ? START_ANCHOR_ATTR : END_ANCHOR_ATTR
	const overridePoint = parseAnchorOverride(segment, overrideAttribute)
	if (overridePoint) return overridePoint

	const path = getFirstUsablePath(segment)
	if (!path) return null

	try {
		const totalLength = path.getTotalLength()
		const pointAt = effectiveType === 'start' ? 0 : totalLength
		return toViewportPointFromPath(path, pointAt)
	} catch {
		return null
	}
}

const resolveTerminalPoint = (terminalElement, terminalAnchorAttr) => {
	const rect = terminalElement.getBoundingClientRect()
	if (rect.width <= 0 || rect.height <= 0) return null

	const parsedAnchor = parseNormalizedAnchor(terminalElement.getAttribute(terminalAnchorAttr))
	const normalizedX = parsedAnchor?.x ?? 0.5
	const normalizedY = parsedAnchor?.y ?? 0.5

	return {
		x: rect.left + rect.width * normalizedX,
		y: rect.top + rect.height * normalizedY,
	}
}

const toWrapperLocalPoint = (point, wrapper) => {
	const rect = wrapper.getBoundingClientRect()
	return {
		x: point.x - rect.left,
		y: point.y - rect.top,
	}
}

const formatCoord = (value) => Number(value.toFixed(3))

const resolvePathBaseStrokeWidth = (path) => {
	const attributeStrokeWidth = parsePositiveNumber(path.getAttribute('stroke-width'))
	if (attributeStrokeWidth) return attributeStrokeWidth

	const computedStrokeWidth = parsePositiveNumber(window.getComputedStyle(path).strokeWidth)
	if (computedStrokeWidth) return computedStrokeWidth

	return null
}

const resolvePathRenderScale = (path) => {
	const matrix = path.getScreenCTM?.()
	if (!matrix) return 1

	const scaleX = Math.hypot(matrix.a, matrix.b)
	const scaleY = Math.hypot(matrix.c, matrix.d)
	const hasScaleX = Number.isFinite(scaleX) && scaleX > 0
	const hasScaleY = Number.isFinite(scaleY) && scaleY > 0

	if (hasScaleX && hasScaleY) return (scaleX + scaleY) * 0.5
	if (hasScaleX) return scaleX
	if (hasScaleY) return scaleY

	return 1
}

const resolveSegmentRenderedStrokeWidth = (segment, fallbackWidth) => {
	const path = getFirstUsablePath(segment)
	if (!path) return fallbackWidth

	const baseStrokeWidth = resolvePathBaseStrokeWidth(path)
	if (!baseStrokeWidth) return fallbackWidth

	return baseStrokeWidth * resolvePathRenderScale(path)
}

const resolveConnectorStrokeWidth = (segments, fallbackWidth) => {
	const resolvedWidths = segments.map((segment) => resolveSegmentRenderedStrokeWidth(segment, null)).filter((value) => Number.isFinite(value) && value > 0)

	if (!resolvedWidths.length) return fallbackWidth

	const totalWidth = resolvedWidths.reduce((sum, value) => sum + value, 0)
	return totalWidth / resolvedWidths.length
}

const createConnectorOverlay = ({ wrapper, pathDataList, stroke, strokeWidth, connectorClass }) => {
	if (!pathDataList.length) return 0

	const overlay = document.createElementNS(SVG_NS, 'svg')
	overlay.setAttribute(GENERATED_CONNECTOR_ATTR, 'true')
	overlay.setAttribute('aria-hidden', 'true')
	overlay.setAttribute('focusable', 'false')
	overlay.style.position = 'absolute'
	overlay.style.inset = '0'
	overlay.style.width = '100%'
	overlay.style.height = '100%'
	overlay.style.pointerEvents = 'none'
	overlay.style.overflow = 'visible'
	overlay.style.zIndex = '1'
	let generatedPathCount = 0

	for (const pathEntry of pathDataList) {
		const pathData = typeof pathEntry === 'string' ? pathEntry : pathEntry?.d
		if (typeof pathData !== 'string' || !pathData.length) continue

		const entryStrokeWidth = typeof pathEntry === 'string' ? null : parsePositiveNumber(pathEntry?.strokeWidth)
		const effectiveStrokeWidth = entryStrokeWidth ?? strokeWidth

		const path = document.createElementNS(SVG_NS, 'path')
		path.setAttribute('d', pathData)
		path.setAttribute('fill', 'none')
		path.setAttribute('stroke', stroke)
		path.setAttribute('stroke-width', `${effectiveStrokeWidth}`)
		if (connectorClass) path.setAttribute('class', connectorClass)

		const fromOrder = typeof pathEntry === 'string' ? Number.NaN : Number.parseFloat(`${pathEntry?.fromOrder}`)
		const toOrder = typeof pathEntry === 'string' ? Number.NaN : Number.parseFloat(`${pathEntry?.toOrder}`)
		const terminalTarget = typeof pathEntry === 'string' ? '' : `${pathEntry?.terminalTarget || ''}`.trim()

		if (Number.isFinite(fromOrder)) path.setAttribute(CONNECTOR_FROM_ORDER_ATTR, `${fromOrder}`)
		if (Number.isFinite(toOrder)) path.setAttribute(CONNECTOR_TO_ORDER_ATTR, `${toOrder}`)
		if (terminalTarget) path.setAttribute(CONNECTOR_TERMINAL_TARGET_ATTR, terminalTarget)

		overlay.appendChild(path)
		generatedPathCount += 1
	}

	if (!generatedPathCount) return 0
	wrapper.appendChild(overlay)
	return generatedPathCount
}

export const useSegmentConnectors = ({
	rootRef,
	loader = null,
	wrapperSelector = '.segments-wrapper',
	segmentSelector = '[data-segment-order]',
	terminalSelector = `[${TERMINAL_ID_ATTR}]`,
	segmentTerminalTargetAttr = TERMINAL_TARGET_ATTR,
	terminalIdAttr = TERMINAL_ID_ATTR,
	terminalAnchorAttr = TERMINAL_ANCHOR_ATTR,
	minDesktopWidth = breakpoints.xsmall + 1,
	profileResolver = () => (window.innerWidth >= minDesktopWidth ? 'desktop' : 'mobile'),
	enabledProfiles = ['desktop'],
	segmentProfileAttr = SEGMENT_PROFILE_ATTR,
	terminalProfileAttr = TERMINAL_PROFILE_ATTR,
	stroke = '#0551F5',
	strokeWidth = 1.5,
	connectorClass = 'segment-connector-path',
	onRefreshComplete = null,
	isRefreshEnabled = null,
} = {}) => {
	let isDestroyed = false
	let resizeRefreshRafId = null
	let refreshRetryRafId = null
	let hasBoundLoaderEvents = false

	const getRoot = () => (rootRef?.value instanceof HTMLElement ? rootRef.value : null)

	const clearGeneratedConnectors = (root) => {
		root.querySelectorAll(`[${GENERATED_CONNECTOR_ATTR}]`).forEach((node) => node.remove())
	}

	const emitRefreshComplete = (root, payload = {}) => {
		if (typeof onRefreshComplete !== 'function') return
		if (!(root instanceof HTMLElement)) return

		try {
			onRefreshComplete({ root, ...payload })
		} catch {
			// Intentionally swallow callback errors to keep connector refresh resilient.
		}
	}

	const resolveLoaderFromContext = () => {
		const explicitLoader = unref(loader)
		if (explicitLoader) return explicitLoader

		const root = getRoot()
		if (root) {
			const contexts = getAllContexts()
			for (const pageContext of contexts.values()) {
				if (pageContext?.element?.value === root) return pageContext?.loader || null
			}
		}

		return getActiveContext()?.loader || null
	}

	const hasGeneratedConnectors = () => {
		const root = getRoot()
		if (!root) return false

		return !!root.querySelector(`[${GENERATED_CONNECTOR_ATTR}]`)
	}

	const refresh = () => {
		if (!import.meta.client || isDestroyed) return

		const root = getRoot()
		if (!root) return
		if (typeof isRefreshEnabled === 'function') {
			let shouldRefresh = true
			try {
				shouldRefresh = !!isRefreshEnabled({ root, width: window.innerWidth })
			} catch {
				shouldRefresh = true
			}
			if (!shouldRefresh) return
		}

		clearGeneratedConnectors(root)

		const resolvedProfile = profileResolver?.({ root, width: window.innerWidth, minDesktopWidth })
		const activeProfile = typeof resolvedProfile === 'string' ? resolvedProfile.trim().toLowerCase() : ''
		const profileSet = new Set((Array.isArray(enabledProfiles) ? enabledProfiles : []).map((profile) => (typeof profile === 'string' ? profile.trim().toLowerCase() : '')).filter(Boolean))

		if (!activeProfile || !profileSet.size || !profileSet.has(activeProfile)) {
			emitRefreshComplete(root, {
				activeProfile,
				profileEnabled: false,
				segmentCount: 0,
				generatedPathCount: 0,
			})
			return
		}

		const segmentEntries = Array.from(root.querySelectorAll(segmentSelector))
			.filter((element) => element instanceof SVGSVGElement)
			.filter((segment) => isElementVisible(segment))
			.filter((segment) => matchesProfile(segment, segmentProfileAttr, activeProfile))
			.map((segment) => ({
				segment,
				order: Number.parseFloat(segment.getAttribute('data-segment-order') || ''),
			}))
			.filter((entry) => Number.isFinite(entry.order))
			.sort((a, b) => a.order - b.order)

		const connectorsByWrapper = new Map()

		for (let index = 0; index < segmentEntries.length - 1; index += 1) {
			const fromEntry = segmentEntries[index]
			const toEntry = segmentEntries[index + 1]
			const fromSegment = fromEntry.segment
			const toSegment = toEntry.segment

			const startPoint = resolveAnchorPoint(fromSegment, 'end')
			const endPoint = resolveAnchorPoint(toSegment, 'start')
			if (!startPoint || !endPoint) continue

			const startWrapper = fromSegment.closest(wrapperSelector)
			if (!(startWrapper instanceof HTMLElement) || !isElementVisible(startWrapper)) continue

			const startLocal = toWrapperLocalPoint(startPoint, startWrapper)
			const endLocal = toWrapperLocalPoint(endPoint, startWrapper)
			const pathData = `M ${formatCoord(startLocal.x)} ${formatCoord(startLocal.y)} L ${formatCoord(endLocal.x)} ${formatCoord(endLocal.y)}`
			const connectorStrokeWidth = resolveConnectorStrokeWidth([fromSegment, toSegment], strokeWidth)

			const existingPaths = connectorsByWrapper.get(startWrapper) || []
			existingPaths.push({
				d: pathData,
				strokeWidth: connectorStrokeWidth,
				fromOrder: fromEntry.order,
				toOrder: toEntry.order,
			})
			connectorsByWrapper.set(startWrapper, existingPaths)
		}

		const terminalMap = new Map()
		const terminals = Array.from(root.querySelectorAll(terminalSelector))
		for (const terminalElement of terminals) {
			if (!(terminalElement instanceof Element)) continue
			if (!isElementVisible(terminalElement)) continue
			if (!matchesProfile(terminalElement, terminalProfileAttr, activeProfile)) continue

			const terminalId = getAttrTrimmed(terminalElement, terminalIdAttr)
			if (!terminalId || terminalMap.has(terminalId)) continue
			terminalMap.set(terminalId, terminalElement)
		}

		for (const fromEntry of segmentEntries) {
			const fromSegment = fromEntry.segment
			const terminalId = getAttrTrimmed(fromSegment, segmentTerminalTargetAttr)
			if (!terminalId) continue

			const terminalElement = terminalMap.get(terminalId)
			if (!(terminalElement instanceof Element)) continue

			const startPoint = resolveAnchorPoint(fromSegment, 'end')
			const endPoint = resolveTerminalPoint(terminalElement, terminalAnchorAttr)
			if (!startPoint || !endPoint) continue

			const startWrapper = fromSegment.closest(wrapperSelector)
			if (!(startWrapper instanceof HTMLElement) || !isElementVisible(startWrapper)) continue

			const startLocal = toWrapperLocalPoint(startPoint, startWrapper)
			const endLocal = toWrapperLocalPoint(endPoint, startWrapper)
			const pathData = `M ${formatCoord(startLocal.x)} ${formatCoord(startLocal.y)} L ${formatCoord(endLocal.x)} ${formatCoord(endLocal.y)}`
			const connectorStrokeWidth = resolveConnectorStrokeWidth([fromSegment], strokeWidth)

			const existingPaths = connectorsByWrapper.get(startWrapper) || []
			existingPaths.push({
				d: pathData,
				strokeWidth: connectorStrokeWidth,
				fromOrder: fromEntry.order,
				terminalTarget: terminalId,
			})
			connectorsByWrapper.set(startWrapper, existingPaths)
		}

		if (!connectorsByWrapper.size) {
			emitRefreshComplete(root, {
				activeProfile,
				profileEnabled: true,
				segmentCount: segmentEntries.length,
				generatedPathCount: 0,
			})
			return
		}

		let generatedPathCount = 0
		connectorsByWrapper.forEach((pathDataList, wrapper) => {
			generatedPathCount += createConnectorOverlay({
				wrapper,
				pathDataList,
				stroke,
				strokeWidth,
				connectorClass,
			})
		})

		emitRefreshComplete(root, {
			activeProfile,
			profileEnabled: true,
			segmentCount: segmentEntries.length,
			generatedPathCount,
		})
	}

	const clearRefreshRetry = () => {
		if (!refreshRetryRafId) return
		cancelAnimationFrame(refreshRetryRafId)
		refreshRetryRafId = null
	}

	const refreshWithRetry = (maxFrames = 8) => {
		if (!import.meta.client || isDestroyed) return

		clearRefreshRetry()
		let remainingFrames = Number.isFinite(maxFrames) ? Math.max(0, Math.floor(maxFrames)) : 0

		const run = () => {
			if (!import.meta.client || isDestroyed) {
				refreshRetryRafId = null
				return
			}

			refresh()

			if (hasGeneratedConnectors() || remainingFrames <= 0) {
				refreshRetryRafId = null
				return
			}

			remainingFrames -= 1
			refreshRetryRafId = requestAnimationFrame(run)
		}

		run()
	}

	const bindLoaderEvents = (resolvedLoader) => {
		if (!resolvedLoader || hasBoundLoaderEvents) return

		hasBoundLoaderEvents = true
		resolvedLoader.loaded?.then(() => refreshWithRetry(8))
		resolvedLoader.transitionReady?.then(() => refreshWithRetry(12))
		resolvedLoader.ready?.then(() => refreshWithRetry(12))
	}

	const onWindowResize = () => {
		refreshWithRetry(2)

		if (resizeRefreshRafId) cancelAnimationFrame(resizeRefreshRafId)
		resizeRefreshRafId = requestAnimationFrame(() => {
			resizeRefreshRafId = null
			refreshWithRetry(2)
		})
	}

	const destroy = () => {
		if (!import.meta.client || isDestroyed) return
		isDestroyed = true

		window.removeEventListener('resize', onWindowResize)
		window.removeEventListener('orientationchange', onWindowResize)
		if (resizeRefreshRafId) {
			cancelAnimationFrame(resizeRefreshRafId)
			resizeRefreshRafId = null
		}
		clearRefreshRetry()
	}

	onMounted(() => {
		if (!import.meta.client || isDestroyed) return

		window.addEventListener('resize', onWindowResize, { passive: true })
		window.addEventListener('orientationchange', onWindowResize, { passive: true })
		bindLoaderEvents(resolveLoaderFromContext())

		nextTick().then(() => {
			bindLoaderEvents(resolveLoaderFromContext())
			refreshWithRetry(8)
			document.fonts?.ready?.then(() => refreshWithRetry(8))
		})
	})

	onBeforeUnmount(() => {
		destroy()
	})

	return {
		refresh,
		destroy,
	}
}
